import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Compass, X } from "lucide-react";
import type { Map as MapLibreMap } from "maplibre-gl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { chromeSelectClass } from "@/lib/map-chrome";
import { cn } from "@/lib/utils";

type VisualizerMapCompassProps = {
  map: MapLibreMap | null;
};

const DIRECTIONS: { label: string; bearing: number }[] = [
  { label: "С", bearing: 0 },
  { label: "СВ", bearing: 45 },
  { label: "В", bearing: 90 },
  { label: "ЮВ", bearing: 135 },
  { label: "Ю", bearing: 180 },
  { label: "ЮЗ", bearing: 225 },
  { label: "З", bearing: 270 },
  { label: "СЗ", bearing: 315 },
];

function normalizeBearing(value: number): number {
  const wrapped = ((value % 360) + 360) % 360;
  return Math.round(wrapped * 10) / 10;
}

function bearingFromPointer(clientX: number, clientY: number, rect: DOMRect): number {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const rad = Math.atan2(clientX - cx, cy - clientY);
  return normalizeBearing((rad * 180) / Math.PI);
}

export function VisualizerMapCompass({ map }: VisualizerMapCompassProps) {
  const [open, setOpen] = useState(false);
  const [bearing, setBearing] = useState(0);
  const dialRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map) {
      setBearing(0);
      return;
    }
    const sync = () => setBearing(normalizeBearing(map.getBearing()));
    sync();
    map.on("rotate", sync);
    map.on("rotateend", sync);
    return () => {
      map.off("rotate", sync);
      map.off("rotateend", sync);
    };
  }, [map]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const applyBearing = (next: number, animate = true) => {
    if (!map) return;
    const value = normalizeBearing(next);
    setBearing(value);
    if (animate) map.easeTo({ bearing: value, duration: 280 });
    else map.setBearing(value);
  };

  const startDialDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!map || !dialRef.current) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    const dial = dialRef.current;
    try {
      dial.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    const move = (ev: PointerEvent) => {
      const rect = dial.getBoundingClientRect();
      applyBearing(bearingFromPointer(ev.clientX, ev.clientY, rect), false);
    };
    const up = (ev: PointerEvent) => {
      try {
        if (dial.hasPointerCapture(ev.pointerId)) dial.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.removeEventListener("pointercancel", up);
    };
    move(event.nativeEvent);
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("pointercancel", up);
  };

  return (
    <div className={cn("pointer-events-auto absolute right-3 top-52 z-[110] flex flex-col items-end gap-2", chromeSelectClass)}>
      <Button
        type="button"
        size="icon"
        variant={open ? "default" : "secondary"}
        className="h-10 w-10 rounded-full shadow-md"
        disabled={!map}
        aria-label="Компас поворота карты"
        title="Поворот карты"
        aria-pressed={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="relative flex h-6 w-6 items-center justify-center">
          <Compass className="h-5 w-5" style={{ transform: `rotate(${-bearing}deg)` }} />
        </span>
      </Button>

      {open && (
        <div className="w-64 rounded-md border bg-card/95 p-3 shadow-md backdrop-blur">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">Поворот карты</h2>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              aria-label="Закрыть компас"
              title="Закрыть"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="mb-3 grid grid-cols-4 gap-1">
            {DIRECTIONS.map((item) => (
              <Button
                key={item.label}
                type="button"
                size="sm"
                variant={Math.abs(normalizeBearing(bearing) - item.bearing) < 1 ? "default" : "outline"}
                className="h-8 px-0 text-xs"
                disabled={!map}
                onClick={() => applyBearing(item.bearing)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          <div
            ref={dialRef}
            className="relative mx-auto mb-2 h-36 w-36 cursor-grab touch-none select-none active:cursor-grabbing"
            onPointerDown={startDialDrag}
            role="slider"
            aria-label="Поворот карты компасом"
            aria-valuemin={0}
            aria-valuemax={359}
            aria-valuenow={Math.round(bearing)}
          >
            <div className="absolute inset-0 rounded-full border-2 border-border bg-muted/40" />
            {DIRECTIONS.map((item) => {
              const rad = (item.bearing * Math.PI) / 180;
              const x = 50 + Math.sin(rad) * 42;
              const y = 50 - Math.cos(rad) * 42;
              return (
                <span
                  key={`tick-${item.label}`}
                  className="pointer-events-none absolute text-[10px] font-medium text-muted-foreground"
                  style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
                >
                  {item.label}
                </span>
              );
            })}
            <div
              className="absolute left-1/2 top-1/2 h-[44%] w-0.5 origin-bottom rounded-full bg-red-500"
              style={{ transform: `translate(-50%, -100%) rotate(${bearing}deg)` }}
            />
            <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border bg-card" />
          </div>
          <p className="mb-2 text-center text-[11px] text-muted-foreground">Перетащите компас для поворота</p>

          <div className="flex items-center justify-center gap-2">
            <Input
              type="number"
              min={0}
              max={359}
              step={1}
              className="h-8 w-20 text-center"
              value={Math.round(bearing)}
              disabled={!map}
              aria-label="Угол поворота в градусах"
              onChange={(event) => {
                const next = Number(event.target.value);
                if (!Number.isFinite(next)) return;
                applyBearing(next, false);
              }}
            />
            <span className="text-xs text-muted-foreground">°</span>
            <Button type="button" size="sm" variant="outline" disabled={!map} onClick={() => applyBearing(0)}>
              Сброс
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
